import type { GetQuickFilterTextParams } from 'ag-grid-community';
import { resolveShowDescription } from '../../AgGrid/AgGridSecurity';
import type { AgGridSecurityDecorationParams } from '../../AgGrid/types';
import { defaultGetQuickFilterText } from '../utils';
import type { AssetColumnProps } from './asset';
import { resolveBlotterAssetIsCurrency } from './resolveBlotterAssetIsCurrency';
import { resolveSecurityColumnShowDescription } from './securityDescription';
import { wrapSecurityIdentifierQuickFilterText } from './securityIdentifiers';

type CurrencyQuickFilterColumnParams = Pick<AssetColumnProps, 'currencies' | 'showDescription'>;

type AssetQuickFilterColumnParams<T extends object> = Pick<
  AssetColumnProps<T>,
  'currencies' | 'showDescription' | 'includeSecurityIdentifiersInQuickFilter'
> & {
  assetTypeField?: string;
};

interface GetDisplayedAssetDescriptionParams<T extends object> {
  symbol: string;
  data: T | undefined;
  context: AgGridSecurityDecorationParams['context'];
  columnParams: AssetQuickFilterColumnParams<T>;
}

function getDisplayedCurrencyDescription(
  symbol: string,
  context: AgGridSecurityDecorationParams['context'],
  columnParams: CurrencyQuickFilterColumnParams
): string | undefined {
  if (columnParams.showDescription === false) {
    return undefined;
  }

  return (columnParams.currencies ?? context.current.currenciesBySymbol)?.get(symbol)?.Description;
}

export function wrapDisplayedCurrencyDescriptionQuickFilter(
  baseGetQuickFilterText: ((params: GetQuickFilterTextParams) => string) | undefined,
  columnParams: CurrencyQuickFilterColumnParams
): (params: GetQuickFilterTextParams) => string {
  const base = baseGetQuickFilterText ?? defaultGetQuickFilterText;

  return params => {
    const baseText = base(params);
    const symbol = params.value;
    if (typeof symbol !== 'string' || symbol === '') {
      return baseText;
    }

    const description = getDisplayedCurrencyDescription(symbol, params.context, columnParams);
    return description && !baseText.includes(description) ? `${baseText} ${description}` : baseText;
  };
}

export function getDisplayedAssetDescription<T extends object>({
  symbol,
  data,
  context,
  columnParams,
}: GetDisplayedAssetDescriptionParams<T>): string | undefined {
  const isCurrency = resolveBlotterAssetIsCurrency({ context, data, value: symbol }, columnParams.assetTypeField);

  if (isCurrency) {
    return getDisplayedCurrencyDescription(symbol, context, columnParams);
  }

  return getDisplayedSecurityDescription({ symbol, data, context, columnParams });
}

function getDisplayedSecurityDescription<T extends object>({
  symbol,
  data,
  context,
  columnParams,
}: GetDisplayedAssetDescriptionParams<T>): string | undefined {
  const security = context.current.securitiesBySymbol?.get(symbol);
  const decorationParams = { context, data, security };
  if (resolveShowDescription(resolveSecurityColumnShowDescription(columnParams), decorationParams) !== true) {
    return undefined;
  }

  return security?.Description;
}

export function wrapDisplayedAssetDescriptionQuickFilter<T extends object>(
  baseGetQuickFilterText: ((params: GetQuickFilterTextParams) => string) | undefined,
  columnParams: AssetQuickFilterColumnParams<T>
) {
  return wrapDisplayedDescriptionQuickFilter(baseGetQuickFilterText, params =>
    getDisplayedAssetDescription({
      symbol: params.value,
      data: params.data,
      context: params.context,
      columnParams,
    })
  );
}

export function wrapDisplayedSecurityDescriptionQuickFilter(
  baseGetQuickFilterText: ((params: GetQuickFilterTextParams) => string) | undefined,
  columnParams: Pick<AssetColumnProps, 'showDescription'>
) {
  return wrapDisplayedDescriptionQuickFilter(baseGetQuickFilterText, params =>
    getDisplayedSecurityDescription({
      symbol: params.value,
      data: params.data,
      context: params.context,
      columnParams,
    })
  );
}

function wrapDisplayedDescriptionQuickFilter(
  baseGetQuickFilterText: ((params: GetQuickFilterTextParams) => string) | undefined,
  getDescription: (params: GetQuickFilterTextParams) => string | undefined
) {
  const base = baseGetQuickFilterText ?? defaultGetQuickFilterText;

  return (params: GetQuickFilterTextParams): string => {
    const baseText = base(params);
    const symbol = params.value;
    if (typeof symbol !== 'string' || symbol === '') {
      return baseText;
    }

    const description = getDescription(params);

    return description && !baseText.includes(description) ? `${baseText} ${description}` : baseText;
  };
}

export function createGetAssetColumnQuickFilterText<T extends object>(columnParams: AssetQuickFilterColumnParams<T>) {
  let getQuickFilterText = wrapDisplayedAssetDescriptionQuickFilter(undefined, columnParams);
  if (columnParams.includeSecurityIdentifiersInQuickFilter === true) {
    getQuickFilterText = wrapSecurityIdentifierQuickFilterText(getQuickFilterText);
  }
  return getQuickFilterText;
}
